import { X } from "lucide-react";
import { useWantedStatusQuery } from "@/hooks/roster-craete/useWantedStatusQuery";
import { useScheduleStore } from "@/store/scheduleStore";
import { useIsAdmin, useLoginUser } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import * as mutate from "@script/mutate";
import { format } from "date-fns";
import httpClient from "@/config/axios";
import { toast } from "@/components/toast/CustomToast";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

interface WantedSelectorProps {
  selectedGroupId?: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSettings?: () => void;
  onWantedConfig?: () => void;
}

export default function WantedSelectorModal({
  selectedGroupId,
  isOpen,
  onClose,
  onSettings,
  onWantedConfig,
}: WantedSelectorProps) {
  const { curDate } = useScheduleStore();
  const isAdmin = useIsAdmin();
  const loginUser = useLoginUser();
  // ADM=드롭다운 선택, HN/일반=현재 병동 토큰(group_id). 빈 문자열이면 백엔드 home 폴백.
  const gid = isAdmin ? (selectedGroupId ?? "") : (loginUser?.group_id ?? "");
  const queryClient = useQueryClient();

  // get Wanted
  const { data: wantedStatus } = useWantedStatusQuery({
    year: curDate?.year,
    month: curDate?.month,
    groupId: gid,
  });

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [deadlineDate, setDeadlineDate] = useState<Date | null>(null);
  const [dialogContentElement, setDialogContentElement] =
    useState<HTMLDivElement | null>(null);

  // wantedStatus 변경 시 deadlineDate 업데이트
  useEffect(() => {
    // console.log("wantedStatus:", wantedStatus);
    // console.log("wantedStatus?.exp_date:", wantedStatus?.exp_date);
    // console.log("wantedStatus?.status:", wantedStatus?.status);

    if (wantedStatus?.exp_date) {
      // API 응답은 문자열로 오므로 항상 new Date()로 변환
      const date =
        typeof wantedStatus.exp_date === "string"
          ? new Date(wantedStatus.exp_date)
          : wantedStatus.exp_date instanceof Date
            ? wantedStatus.exp_date
            : new Date(wantedStatus.exp_date);

      // 유효한 날짜인지 확인
      if (!isNaN(date.getTime())) {
        setDeadlineDate(date);
      } else {
        console.warn("유효하지 않은 날짜:", wantedStatus.exp_date);
        setDeadlineDate(null);
      }
    } else {
      setDeadlineDate(null);
    }
  }, [wantedStatus?.exp_date, wantedStatus?.status]);

  // datePickerOpen이 열릴 때 초기 날짜 설정
  useEffect(() => {
    if (datePickerOpen) {
      if (wantedStatus?.exp_date) {
        setDeadlineDate(
          wantedStatus.exp_date instanceof Date
            ? wantedStatus.exp_date
            : new Date(wantedStatus.exp_date)
        );
      } else {
        setDeadlineDate(null);
      }
    }
  }, [datePickerOpen, wantedStatus?.exp_date]);

  // 원티드 요청/변경 Mutation
  const { mutate: requestWantedMutation, isPending: isRequestPending } =
    useMutation({
      mutationFn: async () => {
        if (!curDate?.year || !curDate?.month) return;

        if (wantedStatus?.status === "requested") {
          await mutate.saveChangeWantedDeadline(
            curDate.year,
            curDate.month,
            deadlineDate,
            gid
          );
        } else {
          await mutate.requestWantedDeadline(
            curDate.year,
            curDate.month,
            deadlineDate,
            gid
          );
        }
      },
      onSuccess: async () => {
        if (!curDate?.year || !curDate?.month) return;

        toast.success(
          wantedStatus?.status === "requested"
            ? "원티드 마감일이 변경되었습니다."
            : "원티드 요청되었습니다."
        );

        // 원티드 상태 갱신
        await queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.wanted.status(curDate.year, curDate.month, gid),
        });

        // 알람 리스트 갱신
        await queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.alarms.list(),
        });
        setDatePickerOpen(false);
      },
    });

  // 원티드 마감 Mutation
  const { mutate: closeWantedMutation, isPending: isClosePending } =
    useMutation({
      mutationFn: async () => {
        if (!curDate?.year || !curDate?.month) return;

        const params = new URLSearchParams({
          year: String(curDate.year),
          month: String(curDate.month),
        });

        if (gid) {
          params.append("group_id", gid);
        }

        await httpClient.patch(`/wanted/close?${params.toString()}`);
      },
      onSuccess: async () => {
        if (!curDate?.year || !curDate?.month) return;

        alert("원티드 요청이 마감되었습니다.");

        // 원티드 상태 갱신
        await queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.wanted.status(curDate.year, curDate.month, gid),
        });

        // 알람 리스트 갱신
        await queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.alarms.list(),
        });
      },
      onError: (error) => {
        alert("에러가 발생했습니다.");
        console.error(error);
      },
    });

  const handleRequestSubmit = () => {
    if (!curDate?.year || !curDate?.month) return;
    if (!confirm("요청하시겠습니까")) return;
    requestWantedMutation();
  };

  const handleCloseWanted = () => {
    if (!curDate?.year || !curDate?.month) return;
    if (!confirm("원티드 요청을 마감하시겠습니까?")) return;
    closeWantedMutation();
  };

  const isSubmitting = isRequestPending || isClosePending;
  const shouldKeepDialogOpen = (target: EventTarget | null) =>
    isSubmitting ||
    (target instanceof Element &&
      target.closest("[data-slot='popover-content']") !== null);

  // requested 일 경우 작성가능
  const isBeforeRequest =
    wantedStatus?.status !== "requested" && wantedStatus?.status !== "closed";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
    >
      <DialogContent
        hideCloseButton
        overlayClassName="bg-black/45"
        ref={setDialogContentElement}
        className="w-[min(400px,calc(100vw-32px))] gap-0 overflow-visible rounded-lg border-0 bg-transparent p-0 font-pretendard shadow-[0_22px_70px_rgba(15,23,42,0.22)]"
        onInteractOutside={(event) => {
          if (shouldKeepDialogOpen(event.target)) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (shouldKeepDialogOpen(event.target)) event.preventDefault();
        }}
        onFocusOutside={(event) => {
          if (shouldKeepDialogOpen(event.target)) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (isSubmitting) event.preventDefault();
        }}
      >
        <div className="overflow-hidden rounded-lg bg-background">
          <DialogHeader className="relative !bg-[#f0f0f2] px-6 py-4 text-center">
            <DialogTitle className="text-center text-[18px] font-semibold text-[#333333]">
              원티드
            </DialogTitle>
            <button
              type="button"
              onClick={() => !isSubmitting && onClose()}
              disabled={isSubmitting}
              className="absolute top-1/2 right-5 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="닫기"
            >
              <X className="h-5 w-5" />
            </button>
          </DialogHeader>

          {/* 버튼 영역 */}
          <div className="flex flex-col gap-3 px-[46px] py-[20px] ">
            {/* 제출 상태일 때 현재 날짜 표시 */}
            {(wantedStatus?.status === "requested" ||
              wantedStatus?.status === "closed") && (
              <div className="flex items-center justify-center gap-2">
                <span className="text-base font-pretendard text-zinc-600">
                  원티드 요청 완료
                </span>
                <div
                  className={cn(
                    "px-3 py-1 rounded-full text-xs font-medium",
                    wantedStatus.exp_date
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-600"
                  )}
                >
                  {wantedStatus.exp_date
                    ? format(
                        typeof wantedStatus.exp_date === "string"
                          ? new Date(wantedStatus.exp_date)
                          : wantedStatus.exp_date instanceof Date
                            ? wantedStatus.exp_date
                            : new Date(wantedStatus.exp_date),
                        "yyyy.MM.dd"
                      )
                    : "마감일 없음"}
                </div>
              </div>
            )}

            {/* 원티드 설정 (정책) — 마감일 요청 전에도 설정 가능하므로 비활성화 없음 */}
            {onWantedConfig && (
              <button
                type="button"
                onClick={onWantedConfig}
                className="w-full px-4 text-center font-semibold py-4 border border-[#D6D6DE] text-[#333333] rounded-[10px] bg-white hover:bg-sig hover:text-white transition-colors font-pretendard! text-[15px]"
              >
                원티드 설정
              </button>
            )}

            {/* 원티드 제출 요청하기 / 변경하기 - Popover로 날짜 피커 */}
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    "w-full  px-4 text-center  duration-300! group py-4 border border-[#D6D6DE] hover:bg-sig hover:text-white rounded-[10px] bg-white transition-colors font-pretendard! text-[15px]",
                    wantedStatus?.status === null && "text-sig font-semibold",
                    wantedStatus?.status === "requested" && "text-sig"
                  )}
                >
                  {wantedStatus?.status === "requested" ||
                  wantedStatus?.status === "closed" ? (
                    <>
                      원티드 제출 마감일{" "}
                      {wantedStatus.exp_date ? (
                        <span className="text-sig group-hover:text-white">
                          변경하기
                        </span>
                      ) : (
                        <span className="text-sig group-hover:text-white">
                          요청하기
                        </span>
                      )}
                    </>
                  ) : (
                    "원티드 제출 요청하기"
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent
                container={dialogContentElement}
                className="z-[60] w-auto p-0"
                align="center"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col">
                  {/* 캘린더 - 바로 펼쳐져 있음 */}
                  <div className="px-4">
                    <Calendar
                      mode="single"
                      className="font-pretendard!"
                      selected={deadlineDate || undefined}
                      onSelect={(date) => {
                        if (date) {
                          setDeadlineDate(date);
                        } else {
                          // 날짜 선택 해제 시 null로 설정
                          setDeadlineDate(null);
                        }
                      }}
                      disabled={(date) => {
                        // 오늘 이전 날짜는 비활성화
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const compareDate = new Date(date);
                        compareDate.setHours(0, 0, 0, 0);
                        return compareDate < today;
                      }}
                      captionLayout="label"
                      defaultMonth={
                        curDate?.year && curDate?.month
                          ? new Date(curDate.year, curDate.month - 1, 1)
                          : undefined
                      }
                      formatters={{
                        formatCaption: (date) => {
                          return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
                        },
                      }}
                    />
                  </div>

                  {/* 구분선 */}
                  <div className="border-t border-[#D6D6DE] mx-4 my-2" />

                  {/* 마감일 없음 토글 */}
                  <div className="px-4 pb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-[14px] font-pretendard text-[#333333]">
                        마감일 없음
                      </span>
                      <Switch
                        checked={!deadlineDate}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setDeadlineDate(null);
                          } else {
                            // 토글 해제 시 현재 날짜로 설정
                            setDeadlineDate(new Date());
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/*  요청하기 버튼 */}
                  <div className="px-4 pb-4 flex flex-col gap-2">
                    {wantedStatus?.status === "requested" && (
                      <button
                        type="button"
                        onClick={handleCloseWanted}
                        disabled={isSubmitting}
                        className="w-full px-4 text-center  py-3 text-sig border border-sig rounded-[10px]  transition-colors font-pretendard! text-[15px] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        원티드 요청 마감하기
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={handleRequestSubmit}
                      disabled={isSubmitting}
                      className="w-full px-4 text-center  py-4 bg-sig text-white rounded-[10px] hover:bg-sig/90 transition-colors font-pretendard! text-[15px] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting
                        ? "요청 중..."
                        : wantedStatus?.status === "requested" ||
                            wantedStatus?.status === "closed"
                          ? "원티드 마감일 변경하기"
                          : "요청하기"}
                    </button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* 원티드 관리보드 설정 */}
            <button
              onClick={onSettings}
              className="w-full px-4 text-center py-4 border hover:bg-sig font-semibold hover:text-white border-[#D6D6DE] text-[#333333] rounded-[10px] bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-[#333333] disabled:hover:border-[#D6D6DE] disabled:hover:bg-white font-pretendard! text-[15px] font-medium"
              disabled={isBeforeRequest}
            >
              원티드 관리보드 설정
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
